import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  limit,
  increment,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Users,
  MessageSquare,
  MessageCircle,
  HandHeart,
  Trophy,
  Loader2,
  Send,
  Trash2,
  Search,
  Sparkles,
  Phone,
  Mail,
  MessageCircleMore,
  UserPlus,
  Heart,
  Images,
  Upload,
  X,
  Pin,
  PinOff,
  ChevronLeft,
  ChevronRight,
  Download,
  ImagePlus,
  Folder,
  ArrowLeft,
  Play
} from 'lucide-react';
import { toast } from 'sonner';
import { logAction } from '../../lib/audit';
import {
  fetchAllContributions,
  isConfirmed,
  toDate
} from '../../lib/contributions';
import {
  uploadToCloudinary,
  cloudinaryThumb,
  cloudinaryDownloadUrl,
  cloudinaryVideoPoster,
  cloudinaryVideo,
  isCloudinaryConfigured
} from '../../lib/cloudinary';
import { compressVideo, isVideoFile } from '../../lib/videoCompress';

/* ------------------------------------------------------------------ */
/* Helpers partagés                                                    */
/* ------------------------------------------------------------------ */

// Initiales pour les avatars (ex: "Moctar Tall" -> "MT")
const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';

// Convertit un champ date Firestore (Timestamp | ISO | Date) en Date JS
const asDate = (raw) => {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate();
  return new Date(raw);
};

// Date relative en français ("il y a 5 min", "il y a 2 h", ...)
const relativeDate = (raw) => {
  const d = asDate(raw);
  if (!d) return "à l'instant";
  const diff = Math.floor((Date.now() - d.getTime()) / 1000); // en secondes
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Garde uniquement les chiffres d'un numéro
const digitsOnly = (s = '') => String(s).replace(/[^\d]/g, '');

// Normalise un numéro sénégalais pour wa.me / tel: (indicatif +221 par défaut)
const toIntlNumber = (phone, cc = '221') => {
  let d = digitsOnly(phone);
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2); // 00221... -> 221...
  else if (d.startsWith('0')) d = cc + d.slice(1); // 0XX local -> +221XX
  else if (d.length <= 9) d = cc + d; // 77XXXXXXX nu -> +221...
  return d;
};

const EMOJIS = [
  { key: '🤲', label: 'Amine' },
  { key: '❤️', label: '' },
  { key: '🌙', label: '' }
];

const ROLE_FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'admin', label: 'Administrateurs' },
  { value: 'responsable_commission', label: 'Responsables' },
  { value: 'membre', label: 'Membres' }
];

/* ================================================================== */
/* Petits composants réutilisables                                     */
/* ================================================================== */

// Boutons de contact cliquables (Appeler / WhatsApp / E-mail)
function ContactActions({ phone, email }) {
  const intl = toIntlNumber(phone);
  return (
    <div className="flex flex-wrap gap-1.5 pt-0.5">
      {phone && (
        <a
          href={`tel:+${intl}`}
          aria-label={`Appeler ${phone}`}
          className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300"
        >
          <Phone className="h-3 w-3" /> Appeler
        </a>
      )}
      {phone && intl && (
        <a
          href={`https://wa.me/${intl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Écrire à ${phone} sur WhatsApp`}
          className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-green-500 hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-950/40 dark:hover:text-green-300"
        >
          <MessageCircleMore className="h-3 w-3" /> WhatsApp
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          aria-label={`Envoyer un e-mail à ${email}`}
          className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-sky-400 hover:bg-sky-50 hover:text-sky-700 dark:hover:bg-sky-950/40 dark:hover:text-sky-300"
        >
          <Mail className="h-3 w-3" /> E-mail
        </a>
      )}
    </div>
  );
}

// Squelette générique pour les listes en cours de chargement
function ListSkeleton({ count = 3, className = '' }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="rounded-2xl border-border/50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ================================================================== */
/* Fil de commentaires d'une annonce                                   */
/* ================================================================== */
function CommentThread({ post }) {
  const { user, userData, isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, 'community_posts', post.id, 'comments'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Erreur écoute commentaires:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [post.id]);

  const handleSend = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'community_posts', post.id, 'comments'), {
        authorId: user.uid,
        authorName: userData?.fullName || 'Membre',
        content: body,
        createdAt: serverTimestamp()
      });
      // Compteur best-effort (non bloquant si refusé)
      updateDoc(doc(db, 'community_posts', post.id), {
        commentCount: increment(1)
      }).catch(() => {});
      setText('');
    } catch (err) {
      console.error('Erreur commentaire:', err);
      toast.error("Impossible d'envoyer le commentaire");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (comment) => {
    try {
      await deleteDoc(doc(db, 'community_posts', post.id, 'comments', comment.id));
      updateDoc(doc(db, 'community_posts', post.id), {
        commentCount: increment(-1)
      }).catch(() => {});
    } catch (err) {
      console.error('Erreur suppression commentaire:', err);
      toast.error('Suppression impossible');
    }
  };

  return (
    <div className="mt-1 space-y-3 border-t border-border/50 pt-3">
      {loading ? (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucun commentaire. Soyez le premier à répondre.
        </p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => {
            const canDelete = c.authorId === user.uid || isAdmin;
            return (
              <div key={c.id} className="flex items-start gap-2">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-slate-400 to-slate-600 text-[10px] font-semibold text-white">
                    {getInitials(c.authorName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 rounded-xl bg-muted/60 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold leading-tight">{c.authorName}</p>
                    <span className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">
                        {relativeDate(c.createdAt)}
                      </span>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(c)}
                          aria-label="Supprimer le commentaire"
                          className="text-muted-foreground/70 transition-colors hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm leading-snug">
                    {c.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Saisie d'un commentaire */}
      <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-[10px] font-semibold text-white">
            {getInitials(userData?.fullName)}
          </AvatarFallback>
        </Avatar>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Écrire un commentaire…"
          maxLength={500}
          className="h-9 rounded-full text-sm"
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={sending || !text.trim()}
          aria-label="Envoyer le commentaire"
          className="h-9 w-9 shrink-0 rounded-full"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

/* ================================================================== */
/* ONGLET 1 — Mur (fil d'annonces)                                     */
/* ================================================================== */
const POSTS_PAGE = 25;

function MurTab() {
  const { user, userData, isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [pageSize, setPageSize] = useState(POSTS_PAGE);
  const [openComments, setOpenComments] = useState({}); // { [postId]: bool }
  const [attachments, setAttachments] = useState([]); // [{ file, preview }]
  const [uploadPct, setUploadPct] = useState(0);
  const [postLightbox, setPostLightbox] = useState(null); // { images, index }

  // Temps réel via onSnapshot, trié par date desc, limité (perf)
  useEffect(() => {
    const q = query(
      collection(db, 'community_posts'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Erreur écoute community_posts:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [pageSize]);

  // Épinglés d'abord, puis par date (tri stable : l'ordre date est préservé)
  const orderedPosts = useMemo(
    () => [...posts].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)),
    [posts]
  );

  // Sélection d'images à joindre (prévisualisation locale avant publication)
  const handlePickImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (!isCloudinaryConfigured()) {
      toast.error('Stockage non configuré : impossible de joindre des images.');
      return;
    }
    const next = files.slice(0, 6).map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setAttachments((prev) => [...prev, ...next].slice(0, 6));
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => {
      const target = prev[idx];
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Libère les URLs de prévisualisation au démontage
  useEffect(() => () => attachments.forEach((a) => URL.revokeObjectURL(a.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Publier un nouveau message (avec images optionnelles)
  const handlePublish = async () => {
    const text = content.trim();
    if (!text && attachments.length === 0) return;
    setPublishing(true);
    setUploadPct(0);
    try {
      // Upload des images vers Cloudinary, le cas échéant
      let images = [];
      if (attachments.length) {
        for (let i = 0; i < attachments.length; i++) {
          const res = await uploadToCloudinary(attachments[i].file, {
            onProgress: (p) =>
              setUploadPct(Math.round(((i + p / 100) / attachments.length) * 100))
          });
          images.push({
            url: res.secureUrl,
            publicId: res.publicId,
            width: res.width,
            height: res.height
          });
        }
      }
      await addDoc(collection(db, 'community_posts'), {
        authorId: user.uid,
        authorName: userData?.fullName || 'Membre',
        content: text,
        images,
        createdAt: serverTimestamp(),
        reactions: { '🤲': [], '❤️': [], '🌙': [] },
        commentCount: 0,
        pinned: false
      });
      attachments.forEach((a) => URL.revokeObjectURL(a.preview));
      setAttachments([]);
      setContent('');
      toast.success('Message publié');
    } catch (err) {
      console.error('Erreur publication:', err);
      toast.error(err.message || 'Impossible de publier le message');
    } finally {
      setPublishing(false);
      setUploadPct(0);
    }
  };

  // Toggle d'une réaction (ajoute/retire l'uid)
  const toggleReaction = async (post, emoji) => {
    const current = post.reactions?.[emoji] || [];
    const has = current.includes(user.uid);
    try {
      await updateDoc(doc(db, 'community_posts', post.id), {
        [`reactions.${emoji}`]: has ? arrayRemove(user.uid) : arrayUnion(user.uid)
      });
    } catch (err) {
      console.error('Erreur réaction:', err);
      toast.error('Action impossible');
    }
  };

  // Épingler / désépingler (admin)
  const togglePin = async (post) => {
    try {
      await updateDoc(doc(db, 'community_posts', post.id), {
        pinned: !post.pinned,
        pinnedAt: !post.pinned ? serverTimestamp() : null
      });
      toast.success(post.pinned ? 'Annonce désépinglée' : 'Annonce épinglée en tête');
    } catch (err) {
      console.error('Erreur épinglage:', err);
      toast.error('Action impossible');
    }
  };

  // Suppression (auteur ou admin)
  const handleDelete = async (post) => {
    try {
      await deleteDoc(doc(db, 'community_posts', post.id));
      toast.success('Message supprimé');
      logAction(user.uid, user.email, 'DELETE_COMMUNITY_POST', `Post de ${post.authorName}`, {
        postId: post.id
      });
    } catch (err) {
      console.error('Erreur suppression:', err);
      toast.error('Suppression impossible');
    }
  };

  const remaining = 1000 - content.length;

  return (
    <div className="space-y-6">
      {/* Zone de publication */}
      <Card className="rounded-2xl border-border/50">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-sm font-semibold">
                {getInitials(userData?.fullName)}
              </AvatarFallback>
            </Avatar>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Partagez une annonce, une pensée, un rappel à la communauté…"
              className="min-h-[80px] resize-none rounded-xl"
              maxLength={1000}
            />
          </div>
          {/* Prévisualisation des images jointes */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 pl-0 sm:pl-[52px]">
              {attachments.map((a, idx) => (
                <div key={idx} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border/50">
                  <img src={a.preview} alt="" className="h-full w-full object-cover" />
                  {!publishing && (
                    <button
                      onClick={() => removeAttachment(idx)}
                      className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white transition-colors hover:bg-red-600"
                      aria-label="Retirer l'image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Barre de progression d'upload */}
          {publishing && attachments.length > 0 && (
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <label
                className={`inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  publishing || attachments.length >= 6
                    ? 'cursor-not-allowed text-muted-foreground/50'
                    : 'cursor-pointer text-muted-foreground hover:border-emerald-400 hover:text-emerald-700'
                }`}
                title={attachments.length >= 6 ? 'Maximum 6 images' : 'Joindre des images'}
              >
                <ImagePlus className="h-4 w-4" />
                Photos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handlePickImages}
                  disabled={publishing || attachments.length >= 6}
                />
              </label>
              <span
                className={`text-xs ${remaining < 50 ? 'text-amber-600' : 'text-muted-foreground'}`}
              >
                {remaining} caractères restants
              </span>
            </div>
            <Button
              onClick={handlePublish}
              disabled={publishing || (!content.trim() && attachments.length === 0)}
              className="rounded-xl"
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span className="ml-2">{publishing && attachments.length ? `Envoi… ${uploadPct}%` : 'Publier'}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des posts */}
      {loading ? (
        <ListSkeleton count={3} />
      ) : orderedPosts.length === 0 ? (
        <Card className="rounded-2xl border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground/50" />
            <p className="font-medium">Aucune annonce pour le moment</p>
            <p className="text-sm text-muted-foreground">Soyez le premier à publier un message.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orderedPosts.map((post) => {
            const canDelete = post.authorId === user.uid || isAdmin;
            const commentsShown = !!openComments[post.id];
            return (
              <Card
                key={post.id}
                className={`rounded-2xl transition-shadow ${
                  post.pinned
                    ? 'border-emerald-300/70 bg-emerald-50/40 dark:border-emerald-900/50 dark:bg-emerald-950/10'
                    : 'border-border/50'
                }`}
              >
                <CardContent className="p-4 space-y-3">
                  {post.pinned && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                      <Pin className="h-3.5 w-3.5 fill-current" />
                      Annonce épinglée
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-semibold">
                          {getInitials(post.authorName)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold leading-tight">{post.authorName}</p>
                        <p className="text-xs text-muted-foreground">{relativeDate(post.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-8 w-8 ${
                            post.pinned
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-muted-foreground hover:text-emerald-600'
                          }`}
                          onClick={() => togglePin(post)}
                          aria-label={post.pinned ? 'Désépingler' : 'Épingler'}
                          title={post.pinned ? 'Désépingler' : 'Épingler en tête'}
                        >
                          {post.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(post)}
                          aria-label="Supprimer l'annonce"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {post.content && (
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                      {post.content}
                    </p>
                  )}

                  {/* Images jointes */}
                  {Array.isArray(post.images) && post.images.length > 0 && (
                    <div
                      className={`grid gap-2 ${
                        post.images.length === 1
                          ? 'grid-cols-1'
                          : 'grid-cols-2 sm:grid-cols-3'
                      }`}
                    >
                      {post.images.map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setPostLightbox({ images: post.images, index: i })}
                          className={`group/img relative overflow-hidden rounded-xl border border-border/50 bg-muted ${
                            post.images.length === 1 ? 'aspect-video' : 'aspect-square'
                          }`}
                        >
                          <img
                            src={img.publicId ? cloudinaryThumb(img.publicId, { w: 600, h: 600 }) : img.url}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover/img:scale-105"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Réactions + commentaires */}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {EMOJIS.map(({ key, label }) => {
                      const list = post.reactions?.[key] || [];
                      const active = list.includes(user.uid);
                      return (
                        <button
                          key={key}
                          onClick={() => toggleReaction(post, key)}
                          aria-label={`Réagir ${label || key}`}
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-all ${
                            active
                              ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                              : 'border-border/60 hover:bg-muted'
                          }`}
                        >
                          <span className="text-base leading-none">{key}</span>
                          {label && <span className="text-xs">{label}</span>}
                          {list.length > 0 && (
                            <span className="text-xs font-medium">{list.length}</span>
                          )}
                        </button>
                      );
                    })}

                    <button
                      onClick={() =>
                        setOpenComments((s) => ({ ...s, [post.id]: !s[post.id] }))
                      }
                      aria-expanded={commentsShown}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-all ${
                        commentsShown
                          ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                          : 'border-border/60 hover:bg-muted'
                      }`}
                    >
                      <MessageCircle className="h-4 w-4" />
                      <span className="text-xs font-medium">
                        {post.commentCount > 0
                          ? `${post.commentCount} commentaire${post.commentCount > 1 ? 's' : ''}`
                          : 'Commenter'}
                      </span>
                    </button>
                  </div>

                  {commentsShown && <CommentThread post={post} />}
                </CardContent>
              </Card>
            );
          })}

          {/* Charger plus */}
          {posts.length >= pageSize && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setPageSize((p) => p + POSTS_PAGE)}
              >
                Charger plus d'annonces
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Lightbox des images de publications */}
      {postLightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setPostLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            onClick={(e) => { e.stopPropagation(); setPostLightbox(null); }}
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
          {postLightbox.images.length > 1 && (
            <>
              <span className="absolute left-1/2 top-5 -translate-x-1/2 text-xs font-medium text-white/70">
                {postLightbox.index + 1} / {postLightbox.images.length}
              </span>
              <button
                className="absolute left-2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 sm:left-4"
                onClick={(e) => {
                  e.stopPropagation();
                  setPostLightbox((s) => ({
                    ...s,
                    index: (s.index - 1 + s.images.length) % s.images.length
                  }));
                }}
                aria-label="Image précédente"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                className="absolute right-2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 sm:right-4"
                onClick={(e) => {
                  e.stopPropagation();
                  setPostLightbox((s) => ({
                    ...s,
                    index: (s.index + 1) % s.images.length
                  }));
                }}
                aria-label="Image suivante"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          <img
            src={postLightbox.images[postLightbox.index].url}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-3xl rounded-xl object-contain"
          />
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* ONGLET 2 — Intentions (Du'a)                                        */
/* ================================================================== */
function DuasTab() {
  const { user, userData } = useAuth();
  const [duas, setDuas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [intention, setIntention] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'duas'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDuas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Erreur écoute duas:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const totalAmines = useMemo(
    () => duas.reduce((sum, d) => sum + (d.amines?.length || 0), 0),
    [duas]
  );

  const handleShare = async () => {
    const text = intention.trim();
    if (!text) return;
    setSharing(true);
    try {
      await addDoc(collection(db, 'duas'), {
        authorId: user.uid,
        authorName: userData?.fullName || 'Membre',
        intention: text,
        amines: [],
        createdAt: serverTimestamp()
      });
      setIntention('');
      toast.success('Intention partagée. Qu\'Allah l\'exauce.');
    } catch (err) {
      console.error('Erreur partage intention:', err);
      toast.error('Impossible de partager votre intention');
    } finally {
      setSharing(false);
    }
  };

  // Toggle "Amine"
  const toggleAmine = async (dua) => {
    const has = (dua.amines || []).includes(user.uid);
    try {
      await updateDoc(doc(db, 'duas', dua.id), {
        amines: has ? arrayRemove(user.uid) : arrayUnion(user.uid)
      });
    } catch (err) {
      console.error('Erreur amine:', err);
      toast.error('Action impossible');
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête chaleureux */}
      <Card className="overflow-hidden rounded-2xl border-none bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-white/20 p-3">
                <HandHeart className="h-6 w-6" />
              </div>
              <div>
                <p className="text-lg font-semibold">Mur des intentions de prière</p>
                <p className="text-sm text-white/80">
                  Partagez vos du'a et associez-vous à celles des autres en disant « Amine ».
                </p>
              </div>
            </div>
            {totalAmines > 0 && (
              <div className="hidden shrink-0 text-right sm:block">
                <p className="text-2xl font-bold leading-none">{totalAmines}</p>
                <p className="text-xs text-white/80">Amine dits</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Partager une intention */}
      <Card className="rounded-2xl border-border/50">
        <CardContent className="p-4 space-y-3">
          <Textarea
            value={intention}
            onChange={(e) => setIntention(e.target.value)}
            placeholder="Partagez une intention de prière pour vous ou la communauté…"
            className="min-h-[70px] resize-none rounded-xl"
            maxLength={500}
          />
          <div className="flex justify-end">
            <Button onClick={handleShare} disabled={sharing || !intention.trim()} className="rounded-xl">
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandHeart className="h-4 w-4" />}
              <span className="ml-2">Partager l'intention</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des intentions */}
      {loading ? (
        <ListSkeleton count={3} />
      ) : duas.length === 0 ? (
        <Card className="rounded-2xl border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <HandHeart className="h-10 w-10 text-muted-foreground/50" />
            <p className="font-medium">Aucune intention partagée</p>
            <p className="text-sm text-muted-foreground">Partagez la première du'a.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {duas.map((dua) => {
            const amines = dua.amines || [];
            const active = amines.includes(user.uid);
            return (
              <Card
                key={dua.id}
                className="rounded-2xl border-amber-200/60 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-950/10"
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-gradient-to-br from-amber-500 to-orange-600 text-white text-xs font-semibold">
                        {getInitials(dua.authorName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold leading-tight">{dua.authorName}</p>
                      <p className="text-xs text-muted-foreground">{relativeDate(dua.createdAt)}</p>
                    </div>
                  </div>

                  <p className="whitespace-pre-wrap break-words text-sm italic leading-relaxed">
                    « {dua.intention} »
                  </p>

                  <div className="flex items-center justify-between pt-1">
                    <button
                      onClick={() => toggleAmine(dua)}
                      aria-pressed={active}
                      className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
                        active
                          ? 'border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                          : 'border-border/60 hover:bg-muted'
                      }`}
                    >
                      <span className="text-base leading-none">🤲</span>
                      Amine
                      {amines.length > 0 && <span className="text-xs">· {amines.length}</span>}
                    </button>
                    {amines.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {amines.length} {amines.length > 1 ? 'personnes prient' : 'personne prie'}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* ONGLET 3 — Annuaire                                                 */
/* ================================================================== */
const roleLabel = (role) => {
  if (role === 'admin') return 'Administrateur';
  if (role === 'responsable_commission') return 'Responsable';
  return 'Membre';
};

const roleBadgeClass = (role) => {
  if (role === 'admin') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';
  if (role === 'responsable_commission')
    return 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300';
  return '';
};

export function AnnuaireTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [commissionFilter, setCommissionFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Membres validés : isApproved === true (les admins peuvent avoir isApproved indéfini)
        const approved = all.filter((m) => m.isApproved === true || m.role === 'admin');
        approved.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
        setMembers(approved);
      } catch (err) {
        console.error('Erreur chargement annuaire:', err);
        toast.error("Impossible de charger l'annuaire");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Commissions disponibles (pour le filtre)
  const commissions = useMemo(() => {
    const set = new Set();
    members.forEach((m) => m.commission && set.add(m.commission));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [members]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members.filter((m) => {
      // Filtre rôle
      if (roleFilter !== 'all') {
        const role = m.role || 'membre';
        if (role !== roleFilter) return false;
      }
      // Filtre commission
      if (commissionFilter !== 'all' && m.commission !== commissionFilter) return false;
      // Recherche (nom + téléphone + e-mail + commission)
      if (term) {
        const haystack = [m.fullName, m.phone, m.email, m.commission]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [members, search, roleFilter, commissionFilter]);

  return (
    <div className="space-y-4">
      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (nom, téléphone, commission…)"
          className="pl-9 rounded-xl"
        />
      </div>

      {/* Filtres rôle */}
      <div className="flex flex-wrap items-center gap-2">
        {ROLE_FILTERS.map((r) => (
          <button
            key={r.value}
            onClick={() => setRoleFilter(r.value)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              roleFilter === r.value
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'border-border/60 text-muted-foreground hover:bg-muted'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Filtres commission */}
      {commissions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setCommissionFilter('all')}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              commissionFilter === 'all'
                ? 'border-sky-400 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
                : 'border-border/60 text-muted-foreground hover:bg-muted'
            }`}
          >
            Toutes commissions
          </button>
          {commissions.map((c) => (
            <button
              key={c}
              onClick={() => setCommissionFilter(c)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                commissionFilter === c
                  ? 'border-sky-400 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
                  : 'border-border/60 text-muted-foreground hover:bg-muted'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Compteur */}
      {!loading && (
        <p className="text-xs text-muted-foreground">
          {filtered.length} membre{filtered.length > 1 ? 's' : ''}
          {filtered.length !== members.length && ` sur ${members.length}`}
        </p>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="rounded-2xl border-border/50">
              <CardContent className="flex items-start gap-3 p-4">
                <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <Users className="h-10 w-10 text-muted-foreground/50" />
            <p className="font-medium">Aucun membre trouvé</p>
            <p className="text-sm text-muted-foreground">Ajustez votre recherche ou vos filtres.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {filtered.map((m) => (
            <Card key={m.id} className="rounded-2xl border-border/50 transition-shadow hover:shadow-sm">
              <CardContent className="flex items-start gap-3 p-4">
                <Avatar className="h-12 w-12 shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white font-semibold">
                    {getInitials(m.fullName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold leading-tight truncate">{m.fullName || 'Sans nom'}</p>
                    <Badge variant="secondary" className={`text-[10px] ${roleBadgeClass(m.role)}`}>
                      {roleLabel(m.role)}
                    </Badge>
                  </div>
                  {m.commission && (
                    <p className="text-xs text-muted-foreground">Commission : {m.commission}</p>
                  )}
                  {(m.phone || m.email) && <ContactActions phone={m.phone} email={m.email} />}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* ONGLET 4 — Reconnaissance                                           */
/* ================================================================== */
const MEDALS = ['🥇', '🥈', '🥉'];

function ReconnaissanceTab() {
  const { isAdmin, isResponsable } = useAuth();
  const isStaff = isAdmin || isResponsable;
  const [loading, setLoading] = useState(true);
  const [topContributors, setTopContributors] = useState([]);
  const [newMembers, setNewMembers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();

        // Membres (annuaire) — lisible par tout membre connecté
        const usersSnap = await getDocs(collection(db, 'users'));
        const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // --- Top contributeurs : réservé au staff (les cotisations sont privées) ---
        if (isStaff) {
          const nameById = users.reduce((acc, u) => {
            acc[u.id] = u.fullName || 'Membre';
            return acc;
          }, {});
          const contributions = await fetchAllContributions();
          const totals = {};
          contributions
            .filter((c) => isConfirmed(c) && toDate(c).getFullYear() === year)
            .forEach((c) => {
              if (!c.userId) return;
              totals[c.userId] = (totals[c.userId] || 0) + (c.amount || 0);
            });
          const ranking = Object.entries(totals)
            .map(([userId, total]) => ({ userId, total, name: nameById[userId] || 'Membre' }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 5);
          setTopContributors(ranking);
        }

        // --- Nouveaux membres du mois ---
        const recent = users
          .filter((u) => {
            const d = asDate(u.joinedAt);
            return d && d.getMonth() === now.getMonth() && d.getFullYear() === year;
          })
          .filter((u) => u.isApproved === true || u.role === 'admin')
          .sort((a, b) => (asDate(b.joinedAt)?.getTime() || 0) - (asDate(a.joinedAt)?.getTime() || 0));
        setNewMembers(recent);
      } catch (err) {
        console.error('Erreur chargement reconnaissance:', err);
        toast.error('Impossible de charger le palmarès');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isStaff]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="rounded-2xl border-border/50">
          <CardContent className="space-y-3 p-5">
            <Skeleton className="h-5 w-48" />
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const monthLabel = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Top contributeurs — visible par le staff uniquement */}
      {isStaff && (
      <Card className="rounded-2xl border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Top contributeurs {currentYear}
          </CardTitle>
          <CardDescription>
            Les membres les plus généreux en cotisations confirmées cette année. Qu'Allah les récompense.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topContributors.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Sparkles className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                Aucune cotisation confirmée pour {currentYear} pour le moment.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {topContributors.map((c, i) => (
                <div
                  key={c.userId}
                  className={`flex items-center gap-3 rounded-xl border p-3 ${
                    i < 3
                      ? 'border-amber-200/70 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-950/10'
                      : 'border-border/50'
                  }`}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center text-lg">
                    {i < 3 ? MEDALS[i] : <span className="text-sm font-bold text-muted-foreground">{i + 1}</span>}
                  </div>
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs font-semibold">
                      {getInitials(c.name)}
                    </AvatarFallback>
                  </Avatar>
                  <p className="flex-1 truncate font-medium">{c.name}</p>
                  <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {c.total.toLocaleString()} FCFA
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Nouveaux membres du mois */}
      <Card className="rounded-2xl border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-sky-500" />
            Nouveaux membres
          </CardTitle>
          <CardDescription className="capitalize">{monthLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          {newMembers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Heart className="h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">Aucun nouveau membre ce mois-ci.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {newMembers.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-3 rounded-xl border border-border/50 p-3"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-gradient-to-br from-sky-500 to-indigo-600 text-white text-sm font-semibold">
                      {getInitials(m.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{m.fullName || 'Sans nom'}</p>
                    <p className="text-xs text-muted-foreground">
                      Bienvenue parmi nous ! 🌙
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ================================================================== */
/* ONGLET 5 — Galerie photos (Cloudinary)                              */
/* ================================================================== */
const NO_ALBUM = 'Sans album';

function GalerieTab() {
  const { user, userData, isAdmin } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(''); // '' | 'compress' | 'upload'
  const [caption, setCaption] = useState('');
  const [album, setAlbum] = useState(''); // album cible pour l'envoi
  const [currentAlbum, setCurrentAlbum] = useState(null); // null = vue albums ; sinon nom d'album
  const [lightboxIndex, setLightboxIndex] = useState(null); // index dans `displayPhotos`

  const load = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'gallery'), orderBy('createdAt', 'desc')));
      setPhotos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Erreur chargement galerie:', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Regroupe les photos par album (préserve l'ordre date desc)
  const albums = useMemo(() => {
    const map = new Map();
    for (const p of photos) {
      const name = (p.album || '').trim() || NO_ALBUM;
      if (!map.has(name)) map.set(name, { name, photos: [] });
      map.get(name).photos.push(p);
    }
    // Albums nommés d'abord, "Sans album" en dernier
    return Array.from(map.values()).sort((a, b) => {
      if (a.name === NO_ALBUM) return 1;
      if (b.name === NO_ALBUM) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [photos]);

  // Photos affichées dans la grille (album courant) + suggestions d'albums existants
  const displayPhotos = useMemo(
    () => (currentAlbum ? albums.find((a) => a.name === currentAlbum)?.photos || [] : []),
    [albums, currentAlbum]
  );
  const albumNames = useMemo(
    () => albums.map((a) => a.name).filter((n) => n !== NO_ALBUM),
    [albums]
  );

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i - 1 + displayPhotos.length) % displayPhotos.length)),
    [displayPhotos.length]
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % displayPhotos.length)),
    [displayPhotos.length]
  );

  // Navigation clavier dans la lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!isCloudinaryConfigured()) {
      toast.error('Galerie non configurée : créez le preset Cloudinary « dahira_unsigned ».');
      e.target.value = '';
      return;
    }
    // L'album cible : champ de saisie, ou album en cours de consultation
    const targetAlbum = (album.trim() || (currentAlbum && currentAlbum !== NO_ALBUM ? currentAlbum : '')).trim();
    const MAX_VIDEO_MB = 40;
    const maxBytes = MAX_VIDEO_MB * 1024 * 1024;
    setUploading(true);
    let added = 0;
    try {
      for (const original of files) {
        let file = original;
        const isVid = isVideoFile(file);

        // Vidéo trop lourde : on tente une compression dans le navigateur (ffmpeg.wasm).
        // En cas d'échec ou si elle reste > 40 Mo, on refuse ce fichier (repli).
        if (isVid && file.size > maxBytes) {
          setPhase('compress');
          setProgress(0);
          toast.info(`Compression de « ${original.name} »… (cela peut prendre un moment)`);
          try {
            file = await compressVideo(original, { targetMB: MAX_VIDEO_MB, onProgress: setProgress });
          } catch (err) {
            console.error('Compression vidéo:', err);
            toast.error(`« ${original.name} » : ${err.message || 'compression impossible'}. Réduisez la vidéo (max ${MAX_VIDEO_MB} Mo) puis réessayez.`);
            continue; // fichier suivant
          }
        }

        setPhase('upload');
        setProgress(0);
        const res = await uploadToCloudinary(file, { onProgress: setProgress });
        await addDoc(collection(db, 'gallery'), {
          url: res.secureUrl,
          publicId: res.publicId,
          width: res.width,
          height: res.height,
          resourceType: res.resourceType || (isVid ? 'video' : 'image'),
          duration: res.duration || null,
          caption: caption || '',
          album: targetAlbum,
          authorId: user.uid,
          authorName: userData?.fullName || 'Membre',
          createdAt: serverTimestamp()
        });
        added += 1;
      }
      if (added > 0) {
        toast.success(`${added} média(s) ajouté(s)${targetAlbum ? ` à « ${targetAlbum} »` : ''}`);
        setCaption('');
        setAlbum('');
        if (targetAlbum) setCurrentAlbum(targetAlbum);
        load();
      }
    } catch (err) {
      console.error('Upload galerie:', err);
      toast.error(err.message || "Échec de l'envoi");
    } finally {
      setUploading(false);
      setProgress(0);
      setPhase('');
      e.target.value = '';
    }
  };

  const handleDelete = async (photo) => {
    try {
      await deleteDoc(doc(db, 'gallery', photo.id));
      await logAction(user.uid, user.email, 'DELETE_PHOTO', 'Photo galerie', { publicId: photo.publicId });
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setLightboxIndex(null);
      toast.success('Photo supprimée');
    } catch (e) {
      console.error('Suppression photo:', e);
      toast.error('Erreur lors de la suppression');
    }
  };

  const current = lightboxIndex !== null ? displayPhotos[lightboxIndex] : null;

  // Vignette adaptée au type de média (poster pour les vidéos)
  const thumbUrl = (p, size = 500) =>
    p.resourceType === 'video' && p.publicId
      ? cloudinaryVideoPoster(p.publicId, { w: size, h: size })
      : p.publicId
        ? cloudinaryThumb(p.publicId, { w: size, h: size })
        : p.url;

  return (
    <div className="space-y-5">
      {/* Zone d'envoi */}
      <Card className="rounded-2xl border-border/50">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              placeholder={
                currentAlbum && currentAlbum !== NO_ALBUM
                  ? `Album : ${currentAlbum}`
                  : 'Album (optionnel) — ex : Gamou 2025'
              }
              value={album}
              onChange={(e) => setAlbum(e.target.value)}
              className="sm:max-w-[240px]"
              list="album-suggestions"
              disabled={uploading}
            />
            <datalist id="album-suggestions">
              {albumNames.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <Input
              placeholder="Légende (optionnelle)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="flex-1"
              disabled={uploading}
            />
            <label className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 h-10 text-sm font-semibold text-white cursor-pointer transition-colors ${uploading ? 'bg-emerald-400 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading
                ? `${phase === 'compress' ? 'Compression' : 'Envoi'}… ${progress}%`
                : 'Ajouter photos / vidéos'}
              <input type="file" accept="image/*,video/*" multiple hidden onChange={handleFiles} disabled={uploading} />
            </label>
          </div>
          {uploading && (
            <div className="space-y-1">
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
              {phase === 'compress' && (
                <p className="text-[11px] text-muted-foreground">Compression de la vidéo dans le navigateur… ne fermez pas la page.</p>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Photos et vidéos acceptées. Les vidéos de plus de 40 Mo sont automatiquement compressées (ou refusées si trop lourdes).</p>
          {!isCloudinaryConfigured() && (
            <p className="text-xs text-amber-600">⚠️ Stockage non configuré : créez un preset Cloudinary « unsigned » nommé <code>dahira_unsigned</code>.</p>
          )}
        </CardContent>
      </Card>

      {/* Fil d'Ariane lorsqu'on consulte un album */}
      {currentAlbum && (
        <button
          onClick={() => { setCurrentAlbum(null); setLightboxIndex(null); }}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-emerald-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Tous les albums
          <span className="text-muted-foreground/60">/</span>
          <span className="font-semibold text-foreground">{currentAlbum}</span>
        </button>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-xl" />
          ))}
        </div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <Images className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Aucune photo pour le moment. Partagez les moments forts du Dahira !</p>
        </div>
      ) : !currentAlbum ? (
        /* ---------- Vue : liste des albums ---------- */
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {albums.map((alb) => {
            const cover = alb.photos[0];
            return (
              <button
                key={alb.name}
                onClick={() => { setCurrentAlbum(alb.name); setLightboxIndex(null); }}
                className="group relative aspect-square overflow-hidden rounded-xl border border-border/50 bg-muted text-left"
              >
                {cover ? (
                  <img
                    src={thumbUrl(cover)}
                    alt={alb.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Folder className="h-10 w-10 text-muted-foreground/40" />
                  </div>
                )}
                {cover?.resourceType === 'video' && (
                  <span className="absolute right-2 top-2 rounded-full bg-black/55 p-1.5 text-white">
                    <Play className="h-3.5 w-3.5 fill-current" />
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-3">
                  <div className="flex items-center gap-1.5 text-white">
                    <Folder className="h-4 w-4 shrink-0" />
                    <p className="truncate text-sm font-semibold">{alb.name}</p>
                  </div>
                  <p className="text-[11px] text-white/75">
                    {alb.photos.length} photo{alb.photos.length > 1 ? 's' : ''}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* ---------- Vue : photos d'un album ---------- */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {displayPhotos.map((photo, idx) => (
            <div key={photo.id} className="group relative aspect-square overflow-hidden rounded-xl border border-border/50 bg-muted">
              <img
                src={thumbUrl(photo)}
                alt={photo.caption || `${photo.resourceType === 'video' ? 'Vidéo' : 'Photo'} de ${photo.authorName || 'la communauté'}`}
                loading="lazy"
                onClick={() => setLightboxIndex(idx)}
                className="h-full w-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
              />
              {photo.resourceType === 'video' && (
                <button
                  onClick={() => setLightboxIndex(idx)}
                  aria-label="Lire la vidéo"
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="rounded-full bg-black/55 p-3 text-white transition-transform group-hover:scale-110">
                    <Play className="h-6 w-6 fill-current" />
                  </span>
                </button>
              )}
              {(photo.caption || (photo.authorId === user.uid || isAdmin)) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex items-end justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="min-w-0">
                    {photo.caption && <p className="text-xs text-white font-medium truncate">{photo.caption}</p>}
                    <p className="text-[10px] text-white/70 truncate">{photo.authorName}</p>
                  </div>
                  {(photo.authorId === user.uid || isAdmin) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(photo); }}
                      className="shrink-0 rounded-lg bg-black/40 p-1.5 text-white hover:bg-red-600 transition-colors"
                      aria-label="Supprimer la photo"
                      title="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={current.caption || 'Photo en plein écran'}
        >
          {/* Fermer */}
          <button
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Compteur */}
          {displayPhotos.length > 1 && (
            <span className="absolute top-5 left-1/2 -translate-x-1/2 text-xs font-medium text-white/70">
              {lightboxIndex + 1} / {displayPhotos.length}
            </span>
          )}

          {/* Précédent */}
          {displayPhotos.length > 1 && (
            <button
              className="absolute left-2 sm:left-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="Photo précédente"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {/* Média (image ou vidéo) */}
          <figure className="max-h-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            {current.resourceType === 'video' ? (
              <video
                src={current.publicId ? cloudinaryVideo(current.publicId) : current.url}
                poster={current.publicId ? cloudinaryVideoPoster(current.publicId, { w: 1280, h: 720 }) : undefined}
                controls
                autoPlay
                playsInline
                className="max-h-[78vh] w-auto rounded-xl bg-black object-contain"
              />
            ) : (
              <img
                src={current.url}
                alt={current.caption || `Photo de ${current.authorName || 'la communauté'}`}
                className="max-h-[78vh] w-auto rounded-xl object-contain"
              />
            )}
            <figcaption className="mt-3 flex items-center justify-between gap-3 text-sm text-white/80">
              <div className="min-w-0">
                {current.caption && <p className="truncate font-medium">{current.caption}</p>}
                <p className="truncate text-xs text-white/60">
                  {current.authorName} · {relativeDate(current.createdAt)}
                </p>
              </div>
              <a
                href={
                  current.resourceType === 'video'
                    ? current.url
                    : current.publicId
                      ? cloudinaryDownloadUrl(current.publicId, current.caption)
                      : current.url
                }
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/20"
                aria-label="Télécharger le média"
              >
                <Download className="h-3.5 w-3.5" /> Télécharger
              </a>
            </figcaption>
          </figure>

          {/* Suivant */}
          {displayPhotos.length > 1 && (
            <button
              className="absolute right-2 sm:right-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="Photo suivante"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* PAGE PRINCIPALE                                                     */
/* ================================================================== */
export default function Communaute() {
  return (
    <div className="space-y-6 pb-10">
      {/* Héro */}
      <div className="rounded-2xl md:rounded-3xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 p-4 md:p-6 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="rounded-xl md:rounded-2xl bg-white/20 p-2.5 md:p-3">
            <Users className="h-6 w-6 md:h-7 md:w-7" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Communauté</h1>
            <p className="text-xs md:text-sm text-white/80 hidden sm:block">
              Animez la vie du Dahira : annonces, intentions de prière, annuaire et reconnaissance.
            </p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <Tabs defaultValue="mur" className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-2xl p-1 sm:grid-cols-4">
          <TabsTrigger value="mur" className="rounded-xl py-2">
            <MessageSquare className="mr-1.5 h-4 w-4" />
            Mur
          </TabsTrigger>
          <TabsTrigger value="galerie" className="rounded-xl py-2">
            <Images className="mr-1.5 h-4 w-4" />
            Galerie
          </TabsTrigger>
          <TabsTrigger value="duas" className="rounded-xl py-2">
            <HandHeart className="mr-1.5 h-4 w-4" />
            Intentions
          </TabsTrigger>
          <TabsTrigger value="reconnaissance" className="rounded-xl py-2">
            <Trophy className="mr-1.5 h-4 w-4" />
            Reconnaissance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mur" className="mt-6">
          <MurTab />
        </TabsContent>
        <TabsContent value="galerie" className="mt-6">
          <GalerieTab />
        </TabsContent>
        <TabsContent value="duas" className="mt-6">
          <DuasTab />
        </TabsContent>
        <TabsContent value="reconnaissance" className="mt-6">
          <ReconnaissanceTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
